import { memo, useCallback, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { Box } from '@/components/lib/components';
import { BetslipPick } from '@/feature/betslip-pickem/types';
import { createPlayerEventKey } from '@/feature/betslip-pickem/utils/markets-cache-store-utils';
import {
    AUTO_SCROLL_LIST_ENABLED,
    AutoScrollingListBounce,
    AutoScrollingListBounceRef,
} from '@/feature/fantasy-event-sheets/auto-scrolling-list';
import { useRaceToPlaceStore } from '@/feature/lobby/components/race-to-place/hooks/use-race-to-place-store';
import { common } from '@/styles/styles';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

import { PlayerRowSmall } from '../PlayerRowSmall';
import { RaceToPlaceListProps } from './types';

const LOBBY_PLAYER_IMAGE_SIZE = 42;

const AUTO_SCROLL_LIST_SPACING = 24;

export const RaceToPlaceLobbyList = memo(({ picks }: RaceToPlaceListProps) => {
    const autoScrollingListRef = useRef<AutoScrollingListBounceRef>(null);
    const isR2PCarouselInViewPort = useRaceToPlaceStore(state => state.isR2PCarouselInViewPort);

    const renderItem = useCallback(({ item }: { item: BetslipPick }) => {
        return (
            <PlayerRowSmall
                player={item.player}
                projection={item.projection}
                outcome={item.outcome}
                hideCrossedOutValue
            />
        );
    }, []);

    return (
        <Box pt={'s32'} pb={'s16'} overflow={'hidden'} width="100%">
            {AUTO_SCROLL_LIST_ENABLED ? (
                <AutoScrollingListBounce
                    ref={autoScrollingListRef}
                    style={common.justifyStart}
                    spacing={20}
                    contentContainerStyle={styles.lobbyListContentContainer}
                    frameRate={60}
                    speed={0.2}
                    data={picks}
                    renderItem={renderItem}
                    autostart={isR2PCarouselInViewPort}
                />
            ) : (
                <FlatList
                    contentContainerStyle={styles.lobbyFlatlist}
                    data={picks}
                    horizontal
                    keyExtractor={item => `${createPlayerEventKey(item.player.id, item.eventId)}`}
                    renderItem={renderItem}
                    showsHorizontalScrollIndicator={showScrollIndicator}
                />
            )}
            <SizedBox value={16} />
            <LineSeparator style={styles.lineSeparator} />
        </Box>
    );
});

const styles = StyleSheet.create({
    lobbyFlatlist: {
        gap: AUTO_SCROLL_LIST_SPACING,
        justifyContent: 'flex-start',
        flexGrow: 1,
        paddingHorizontal: 16,
    },
    lobbyListContentContainer: {
        paddingHorizontal: 16,
    },
    playerImage: {
        width: LOBBY_PLAYER_IMAGE_SIZE,
        height: LOBBY_PLAYER_IMAGE_SIZE,
        marginTop: 2,
    },
    placeholderImageContainer: {
        width: LOBBY_PLAYER_IMAGE_SIZE,
        height: LOBBY_PLAYER_IMAGE_SIZE,
        marginTop: 2,
    },
    collegeShirtContainer: {
        overflow: 'hidden',
    },
    lineSeparator: {
        width: '100%',
    },
});
